import { observable, action, computed } from 'mobx';

import { registrationStore, usersStore, translationsStore, profileStore } from '../../../stores';

import { api } from '../../../services';

const Rollbar = window.Rollbar;

class Step_5 {
  @observable
  checking = {
    heartbeat: false,
    payment: false,
    munic: false,
    subscription: false
  };

  @observable
  error = {
    munic: false,
    activation: false,
    heartbeat: false
  };

  @observable
  candownload = false;

  constructor(registrationStore, usersStore, translationsStore, profileStore) {
    this.registrationStore = registrationStore;
    this.usersStore = usersStore;
    this.translationsStore = translationsStore;
    this.profileStore = profileStore;
  }

  @action
  setMyPosition() {
    this.registrationStore.setPosition(5);
  }

  @action
  setError(error) {
    this.error = error;
  }

  set download(true_or_false) {
    this.candownload = true_or_false;
  }

  get download() {
    return this.candownload;
  }

  @computed
  get haserror() {
    return this.error.munic || this.error.heartbeat || this.error.activation;
  }

  sendValidation() {
    return api({ action: 'send validation' }, this.user._id, this.asset.imei);
  }

  sendContract() {
    return api({ action: 'send contracts' }, this.user._id, this.asset.imei);
  }

  @action
  resetState() {
    this.checking = {
      heartbeat: false,
      payment: false,
      munic: false,
      subscription: false
    };
    this.error = {
      munic: false,
      activation: false,
      heartbeat: false
    };
  }

  @computed
  get session() {
    return this.registrationStore.session;
  }

  @computed
  get user() {
    return this.registrationStore.user;
  }

  @computed
  get subscription() {
    try {
      if (this.user) return this.user.assets[0].subscription;
    } catch (error) {
      console.error('error getting subscription');
    }
  }

  @computed
  get asset() {
    try {
      if (this.user) return this.user.assets[0];
    } catch (error) {
      console.error('error getting asset');
    }
  }

  @computed
  get currentStatus() {
    return this.registrationStore.subscription.status;
  }

  @computed
  get car() {
    try {
      if (this.user) return this.user.assets[0].car;
    } catch (error) {
      console.error('error getting car');
    }
  }

  @action
  heartbeat() {
    this.checking = Object.assign({}, this.checking, { heartbeat: true });
    return api({ action: 'heartbeat' }, this.user._id).then(heartbeat => {
      this.checking = Object.assign({}, this.checking, { heartbeat: false });
      if (heartbeat && heartbeat.timestamp && heartbeat.batt_voltage) return {};
      throw { name: 'Device not detected', severity: 'medium', details: [{ message: this.translationsStore.current['shared']['lblDeviceNotInstalledLong'] }] };
    });
  }

  /**
   * Payment is issued
   */
  @action
  activateZuoraSubscription() {
    if (this.registrationStore.subscription.status > 5.1) {
      this.checking = Object.assign({}, this.checking, { payment: true });
      return api({ action: 'activate zuora' }, this.user._id)
        .then(result => {
          this.checking = Object.assign({}, this.checking, { payment: false });
          return result;
        })
        .catch(error => {
          this.checking = Object.assign({}, this.checking, { payment: false });
          throw error;
        });
    } else {
      return Promise.resolve({});
    }
  }

  @action
  sendForDigitalSignature() {
    if (this.registrationStore.subscription.status > 5.1) {
      this.checking = Object.assign({}, this.checking, { payment: true });

      const conditionDocumentName = this.registrationStore.documentName;
      return api({ action: 'create agreement digital' }, this.user._id, this.user.assets[0].imei, conditionDocumentName)
        .then(result => {
          this.checking = Object.assign({}, this.checking, { payment: false });
          return result;
        })
        .catch(error => {
          this.checking = Object.assign({}, this.checking, { payment: false });
          throw error;
        });
    } else {
      return Promise.resolve({});
    }
  }

  @action
  configureMunic() {
    if (this.registrationStore.subscription.status < 5.2 && process.env.REACT_APP_ENV === 'production') {
      this.checking = Object.assign({}, this.checking, { munic: true });
      return api({ action: 'register vehicle' }, this.asset.imei)
        .then(result => {
          if (result && result.success && result.success !== 1) {
            Rollbar.warning(`munic vehicle registration failure - ${this.asset.imei}`, { result }, function(err, data) {
	            if (err) {
		            console.log("Error while reporting error to Rollbar: ", err);
	            } else {
		            console.log("Error successfully reported to Rollbar. UUID:", data.result.uuid);
	            }
            });
          }
          return {};
        })
        .catch(error => {
          Rollbar.warning(`munic vehicle registration failure - ${this.asset.imei}`, error, function(err, data) {
	          if (err) {
		          console.log("Error while reporting error to Rollbar: ", err);
	          } else {
		          console.log("Error successfully reported to Rollbar. UUID:", data.result.uuid);
	          }
          });
          return {};
        })
        .then(() =>
          // subscribe offer to the asset plus create and launch configuration with default DTC and crash detection
          api({ action: 'subscribe offer' }, this.asset.imei, this.car.casenumber)
            .then(result => {
              this.checking = Object.assign({}, this.checking, { munic: false });
              return result;
            })
            .catch(error => {
              this.checking = Object.assign({}, this.checking, { munic: false });
              throw error;
            })
        );
    } else {
      return Promise.resolve({});
    }
  }

  @action
  updateSubscription(subscription) {
    this.checking = Object.assign({}, this.checking, { subscription: true });
    return api({ action: 'update asset' }, this.registrationStore.session, this.registrationStore.assetId, {
      subscription
    }).then(updatedAsset => {
      this.checking = Object.assign({}, this.checking, { subscription: false });
      this.registrationStore.setUserAsset(updatedAsset);
      this.usersStore.addOrModify(this.registrationStore.user);
      this.registrationStore.setSession(this.registrationStore.user._id);
      return {};
    });
  }

  updateStatus(status) {
    return Object.assign({}, this.registrationStore.subscription, {
      status: this.registrationStore.subscription.status > status ? this.registrationStore.subscription.status : status
    });
  }

  createNextStatusAndDeleteSubscription() {
    let subscription = Object.assign({}, this.registrationStore.subscription, {
      status: this.registrationStore.subscription.status > 6 ? this.registrationStore.subscription.status : 6
    });
    delete subscription.zuoraOneTimeRatePlan;
    delete subscription.zuoraRecurringRatePlan;
    return subscription;
  }

  getAssetPlatforms = () => {
    return api({ action: 'get asset' }, this.registrationStore.session, this.registrationStore.assetId)
      .then(response => {
        const { munic_platform, teltonika_platform } = response,
          platforms = Object.assign({}, !!munic_platform ? { munic: munic_platform } : undefined, !!teltonika_platform ? { teltonika: teltonika_platform } : undefined);
        return platforms;
      })
      .catch(error => {
        throw error;
      });
  }
}

export default new Step_5(registrationStore, usersStore, translationsStore, profileStore);



// WEBPACK FOOTER //
// ./src/stores/registrationStore/steps/step_5.js